(() => {
  "use strict";

  const GLOBAL_KEY = "KBBogosSlider";
  const FALLBACK_IMAGE_URL =
    "https://us.kerenbartov.com/cdn/shop/files/Frame_144.png?v=1770191564";

  /*
   * Prevent duplicate initialization if the theme editor
   * reloads this asset.
   */
  if (window[GLOBAL_KEY]?.destroy) {
    window[GLOBAL_KEY].destroy();
  }

  let observer = null;
  let updateScheduled = false;
  let destroyed = false;

  const createElement = (tag, className, text) => {
    const node = document.createElement(tag);
    node.className = className;

    if (text !== undefined) {
      node.textContent = text;
    }

    return node;
  };

  const resolveImageUrl = (image) => {
    const source =
      image?.currentSrc ||
      image?.src ||
      image?.getAttribute("src");

    if (!source) {
      return FALLBACK_IMAGE_URL;
    }

    try {
      return new URL(source, window.location.href).href;
    } catch (error) {
      return source;
    }
  };

  const createGiftCard = (originalItem) => {
    const originalButton = originalItem.querySelector(
      ".bogos-gift-item-add-btn"
    );

    const originalImage = originalItem.querySelector(
      ".bogos-gift-item-image"
    );

    const productTitle =
      originalItem
        .querySelector(".bogos-gift-product-title")
        ?.textContent.trim() || "THE BEAUTY BAG";

    const variant =
      originalItem
        .querySelector(".bogos-gift-variant-title")
        ?.textContent.trim() || "SELECT";

    const originalPrice =
      originalItem
        .querySelector(".bogos-gift-item-compare-price")
        ?.textContent.trim() || "";

    const imageUrl = resolveImageUrl(originalImage);

    const card = createElement(
      "article",
      "kb-complete-card"
    );

    /*
     * Product image
     */
    const imageWrapper = createElement(
      "div",
      "kb-complete-card__image"
    );

    const image = document.createElement("img");
    image.src = imageUrl;
    image.alt = productTitle;
    image.loading = "eager";
    image.decoding = "async";

    image.addEventListener(
      "error",
      () => {
        if (image.src !== FALLBACK_IMAGE_URL) {
          image.src = FALLBACK_IMAGE_URL;
        }
      },
      { once: true }
    );

    imageWrapper.appendChild(image);

    /*
     * Product information
     */
    const content = createElement(
      "div",
      "kb-complete-card__content"
    );

    const title = createElement(
      "h4",
      "kb-complete-card__title",
      productTitle
    );

    /*
     * FREE | SIZE
     */
    const details = createElement(
      "div",
      "kb-complete-card__details"
    );

    const free = createElement(
      "span",
      "kb-complete-card__free",
      "FREE"
    );

    const divider = createElement(
      "span",
      "kb-complete-card__divider"
    );

    divider.setAttribute("aria-hidden", "true");

    const variantLabel = createElement(
      "span",
      "kb-complete-card__variant",
      variant
    );

    details.append(free, divider, variantLabel);

    /*
     * Crossed-out original price
     */
    const priceRow = createElement(
      "div",
      "kb-complete-card__price"
    );

    if (originalPrice) {
      const price = createElement(
        "span",
        "kb-complete-card__original-price",
        originalPrice
      );

      priceRow.appendChild(price);
    } else {
      priceRow.setAttribute("aria-hidden", "true");
    }

    /*
     * Custom add button
     */
    const addButton = createElement(
      "button",
      "kb-complete-card__add",
      "ADD"
    );

    addButton.type = "button";
    addButton.setAttribute(
      "aria-label",
      `Add ${productTitle}, ${variant}`
    );

    const unavailable =
      !originalButton ||
      originalButton.disabled ||
      originalButton.dataset.available === "false";

    if (unavailable) {
      addButton.disabled = true;
    }

    addButton.addEventListener("click", () => {
      if (
        !originalButton ||
        !originalButton.isConnected ||
        addButton.disabled
      ) {
        return;
      }

      addButton.disabled = true;
      addButton.textContent = "ADDING…";

      /*
       * Trigger the genuine BOGOS button.
       * BOGOS remains responsible for the product,
       * variant, cart request, and qualification logic.
       */
      originalButton.click();

      window.setTimeout(() => {
        if (addButton.isConnected) {
          addButton.disabled = false;
          addButton.textContent = "ADD";
        }
      }, 1400);
    });

    content.append(
      title,
      details,
      priceRow,
      addButton
    );

    card.append(imageWrapper, content);

    return {
      card,
      originalButton
    };
  };

  const buildSlider = (container) => {
    if (!container?.isConnected) return;

    const existingSlider = container.querySelector(
      ".kb-complete-slider"
    );

    /*
     * Keep the current custom slider when all original
     * BOGOS buttons are still connected.
     */
    if (
      existingSlider &&
      existingSlider.__kbOriginalButtons?.every(
        (button) => button?.isConnected
      )
    ) {
      return;
    }

    existingSlider?.remove();

    const originalItems = [
      ...container.querySelectorAll(
        ".bogos-offer-gift-item"
      )
    ];

    if (!originalItems.length) return;

    const customSlider = createElement(
      "section",
      "kb-complete-slider"
    );

    customSlider.setAttribute(
      "aria-label",
      "Choose your free gift"
    );

    /*
     * Header and navigation
     */
    const header = createElement(
      "div",
      "kb-complete-slider__header"
    );

    const heading = createElement(
      "h3",
      "kb-complete-slider__heading",
      "CHOOSE YOUR FREE GIFT"
    );

    const navigation = createElement(
      "div",
      "kb-complete-slider__navigation"
    );

    const previous = createElement(
      "button",
      "kb-complete-slider__arrow",
      "‹"
    );

    previous.type = "button";
    previous.setAttribute(
      "aria-label",
      "Previous gift"
    );

    const next = createElement(
      "button",
      "kb-complete-slider__arrow",
      "›"
    );

    next.type = "button";
    next.setAttribute("aria-label", "Next gift");

    navigation.append(previous, next);
    header.append(heading, navigation);

    /*
     * Scrollable gift cards
     */
    const viewport = createElement(
      "div",
      "kb-complete-slider__viewport"
    );

    const track = createElement(
      "div",
      "kb-complete-slider__track"
    );

    const originalButtons = [];

    originalItems.forEach((originalItem) => {
      const result = createGiftCard(originalItem);

      originalButtons.push(result.originalButton);
      track.appendChild(result.card);
    });

    viewport.appendChild(track);
    customSlider.append(header, viewport);

    /*
     * Retain references so we can detect when BOGOS
     * redraws its original slider.
     */
    customSlider.__kbOriginalButtons =
      originalButtons;

    container.classList.add("kb-complete-active");
    container.appendChild(customSlider);

    /*
     * Arrow behavior
     */
    const cardDistance = () => {
      const firstCard = track.querySelector(
        ".kb-complete-card"
      );

      if (!firstCard) {
        return track.clientWidth;
      }

      return (
        firstCard.getBoundingClientRect().width + 9
      );
    };

    const updateNavigation = () => {
      const maximumScroll =
        track.scrollWidth - track.clientWidth;

      previous.disabled = track.scrollLeft <= 3;

      next.disabled =
        maximumScroll <= 3 ||
        track.scrollLeft >= maximumScroll - 3;
    };

    previous.addEventListener("click", () => {
      track.scrollBy({
        left: -cardDistance(),
        behavior: "smooth"
      });
    });

    next.addEventListener("click", () => {
      track.scrollBy({
        left: cardDistance(),
        behavior: "smooth"
      });
    });

    track.addEventListener(
      "scroll",
      updateNavigation,
      { passive: true }
    );

    window.addEventListener(
      "resize",
      updateNavigation,
      { passive: true }
    );

    requestAnimationFrame(updateNavigation);
  };

  const buildAllSliders = () => {
    if (destroyed) return;

    document
      .querySelectorAll(
        ".bogos-gifts-slider-container"
      )
      .forEach(buildSlider);
  };

  const scheduleUpdate = () => {
    if (updateScheduled || destroyed) return;

    updateScheduled = true;

    requestAnimationFrame(() => {
      updateScheduled = false;
      buildAllSliders();
    });
  };

  const start = () => {
    if (destroyed) return;

    buildAllSliders();

    observer = new MutationObserver(scheduleUpdate);

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  const destroy = () => {
    destroyed = true;
    observer?.disconnect();

    document
      .querySelectorAll(".kb-complete-slider")
      .forEach((element) => element.remove());

    document
      .querySelectorAll(".kb-complete-active")
      .forEach((element) => {
        element.classList.remove(
          "kb-complete-active"
        );
      });

    delete window[GLOBAL_KEY];
  };

  window[GLOBAL_KEY] = {
    refresh: buildAllSliders,
    destroy
  };

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      start,
      { once: true }
    );
  } else {
    start();
  }
})();